import React, { useEffect, useRef, useState } from "react";
import "./style.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAgencyInfo } from "context/agency";
import { useAppServices } from "hook/services";
import previewIcon from "../../../assets/themeIcons/preview.svg";
import MDModal from "components/MDModal";

const AppNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { email, locationId } = useParams();
  const [agency] = useAgencyInfo();
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [notification, SetNotification] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cannyAppId, setCannyAppId] = useState("");
  const [hasCannyUnseen, setHasCannyUnseen] = useState(false);
  const cannyInitialized = useRef(false);
  const AppService = useAppServices();
  const currentCompanyId = agency?.ghl?.location_id;

  const routeSuffix = pathname.split("/").filter(Boolean).slice(2).join("/");

  const isNavActive = (key) => {
    switch (key) {
      case "dashboard":
        return routeSuffix === "dashboard" || routeSuffix === "Home";
      case "accounts":
        return routeSuffix === "accounts" || routeSuffix.startsWith("account-detail");
      case "leads":
        return routeSuffix === "leads";
      case "submissions":
        return routeSuffix === "submissions";
      case "consumers":
        return routeSuffix === "consumers" || routeSuffix.startsWith("consumer-detail");
      case "branding":
        return routeSuffix.startsWith("settings/branding");
      case "saas":
        return routeSuffix === "saas" || routeSuffix.startsWith("saas/");
      case "settings":
        return routeSuffix.startsWith("settings/") && !routeSuffix.startsWith("settings/branding");
      default:
        return false;
    }
  };

  const navLinkClass = (key) =>
    `navbar-link${isNavActive(key) ? " navbar-link--active" : ""}`;

  const handleNotification = () => {
    setSidebarOpen(true);
    getNotification();
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const getNotification = async () => {
    const { response } = await AppService.notification.get();

    if (response) {
      SetNotification(response.data);
    }
  };

  useEffect(() => {
    getNotification();
  }, []);

  const isUnread = (n) => !n?.readBy?.includes(currentCompanyId);

  const UnReadNotifications = notification?.filter(isUnread);

  const markAsRead = async (item) => {
    if (!item?._id || !currentCompanyId || !isUnread(item)) return;

    // Optimistic update so the badge clears immediately
    SetNotification((prev) =>
      prev.map((n) =>
        n._id === item._id ? { ...n, readBy: [...(n.readBy || []), currentCompanyId] } : n
      )
    );

    const payload = {
      _id: item._id,
      readBy: [currentCompanyId],
    };

    const { response } = await AppService.notification.read({ payload });

    if (response) {
      getNotification();
    }
  };

  const handleRead = async (item) => {
    setSidebarOpen(false);
    setSelectedNotification(item);
    setModalOpen(true);
    await markAsRead(item);
  };

  // State to store the iframe URL

  // Function to dynamically update iframe URL
  const handleRoutes = (page) => {
    const newIframeUrl = `/${email}/${locationId}/${page}`;
    navigate(newIframeUrl);
  };

  const handleRedirect = () => {
    if (agency?.domain) {
      let formattedUrl = agency.domain.trim(); // Remove extra spaces

      if (!formattedUrl.startsWith("http://") && !formattedUrl.startsWith("https://")) {
        formattedUrl = `https://${formattedUrl}`;
      }

      window.open(formattedUrl, "_blank");
    } else {
      // alert("No valid URL found in agency data.");
      let formattedUrl = `https://previewdirectory.fixmyonline.com/app/${agency._id}`;

      window.open(formattedUrl, "_blank");
    }
  };

  const redirectToHelpCenter = () => {
    let formattedUrl = `https://smartdirectory.ai/help`;
    window.open(formattedUrl, "_blank");
  };

  const GetSuperAdminSetting = async () => {
    const { response } = await AppService.superadmin.GET();
    if (response) {
      setCannyAppId(response?.data?.canny_app_id || "");
    }
  };

  useEffect(() => {
    GetSuperAdminSetting();
  }, []);

  const refreshCannyUnseen = () => {
    if (typeof window.Canny !== "function") return;
    try {
      setHasCannyUnseen(!!window.Canny("hasUnseenEntries"));
    } catch {
      setHasCannyUnseen(false);
    }
  };

  useEffect(() => {
    if (!cannyAppId || typeof window.Canny !== "function" || cannyInitialized.current) return;

    const userId = agency?.ghl?.location_id || locationId || agency?._id;
    if (userId) {
      window.Canny("identify", {
        appID: cannyAppId,
        user: {
          id: String(userId),
          email: email || agency?.email || undefined,
          name: agency?.name || email || "User",
        },
      });
    }

    window.Canny("initChangelog", {
      appID: cannyAppId,
      position: "bottom",
      align: "left",
      theme: "light",
    });

    cannyInitialized.current = true;
    refreshCannyUnseen();

    window.Canny("registerOnChangelogOpenCallback", () => {
      setHasCannyUnseen(false);
    });

    const intervalId = window.setInterval(refreshCannyUnseen, 60000);

    return () => {
      window.clearInterval(intervalId);
      try {
        window.Canny("closeChangelog");
      } catch {
        /* ignore */
      }
    };
  }, [cannyAppId, agency, email, locationId]);

  return (
    <>
      <nav className="navbar">
        <div className="navbar__start">
          <span className="navbar-brand">SmartDirectoryAI</span>
          <ul className="navbar-links navbar-links--primary">
          <li>
            <span className={navLinkClass("dashboard")} onClick={() => handleRoutes("dashboard")}>
              Dashboard
            </span>
          </li>
          <li>
            <span className={navLinkClass("accounts")} onClick={() => handleRoutes("accounts")}>
              Businesses
            </span>
          </li>
          <li>
            <span className={navLinkClass("leads")} onClick={() => handleRoutes("leads")}>
              Leads
            </span>
          </li>
          <li>
            <span className={navLinkClass("submissions")} onClick={() => handleRoutes("submissions")}>
              Submissions
            </span>
          </li>
          <li>
            <span className={navLinkClass("consumers")} onClick={() => handleRoutes("consumers")}>
              Consumers
            </span>
          </li>
          <li>
            <span className={navLinkClass("branding")} onClick={() => handleRoutes("settings/branding")}>
              Branding
            </span>
          </li>
          <li>
            <span className={navLinkClass("saas")} onClick={() => handleRoutes("saas")}>
              Plans
            </span>
          </li>
          <li>
            <span className={navLinkClass("settings")} onClick={() => handleRoutes("settings/integration")}>
              Settings
            </span>
          </li>
          <li>
            <span
              className="navbar-link navbar-link--changelog"
              data-canny-changelog
            >
              Changelogs
              {hasCannyUnseen && (
                <span className="navbar-link__unseen-dot" aria-label="New changelog entries" />
              )}
            </span>
          </li>
          <li>
            <span className="navbar-link" onClick={redirectToHelpCenter}>
              Help
            </span>
          </li>
          <li>
            <span className="navbar-link" onClick={handleNotification}>
              Notification
              {UnReadNotifications?.length > 0 && (
                <span className="navbar-link__badge">{UnReadNotifications.length}</span>
              )}
            </span>
          </li>
          <li className="navbar-links__divider" aria-hidden="true" />
          <li>
            <span className="navbar-link navbar-link--preview" onClick={handleRedirect}>
              Preview{" "}
              <img src={previewIcon} alt="" />
            </span>
          </li>
        </ul>
        </div>
      </nav>
      <div className={`notification-sidebar ${isSidebarOpen ? "active" : ""}`}>
        <div className="notification-header">
          <h4 style={{ display: "flex", alignItems: "center", gap: "8px", margin: 0 }}>
            Notifications
            {UnReadNotifications?.length > 0 && (
              <span
                style={{
                  backgroundColor: "red",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  color: "white",
                  fontSize: "13px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "20px",
                }}
              >
                {UnReadNotifications.length}
              </span>
            )}
          </h4>

          <button
            onClick={closeSidebar}
            style={{
              cursor: "pointer",
              height: "30px",
              width: "30px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "20px",
              border: "none",
            }}
          >
            x
          </button>
        </div>
        <div className="notification-body">
          {notification?.length > 0 ? (
            <>
              {notification.map((n, index) => (
                <>
                  {n?._id === "69832a4b9fea627db894ee66" ? (
                    <div
                      onClick={() => handleRead(n)}
                      key={index}
                      style={{
                        color: "white",
                        borderRadius: "10px",
                        padding: "10px 10px",
                        marginBottom: "10px",
                        backgroundColor: isUnread(n) ? "#ebfbfd" : "white",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                        cursor: "pointer",
                      }}
                    >
                      <p style={{ margin: 0, color: "black", fontSize: "14px", fontWeight: 500 }}>
                        {n.title}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          color: "gray",
                          margin: 0,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        dangerouslySetInnerHTML={{ __html: n.message }}
                      ></p>

                      <p style={{ fontSize: "11px", color: "black", marginTop: "5px" }}>
                        {new Date(n.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <div
                      onClick={() => handleRead(n)}
                      key={index}
                      style={{
                        color: "white",
                        borderRadius: "10px",
                        padding: "10px 10px",
                        marginBottom: "10px",
                        backgroundColor: isUnread(n) ? "#ebfbfd" : "white",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                        cursor: "pointer",
                      }}
                    >
                      <p style={{ margin: 0, color: "black", fontSize: "14px", fontWeight: 500 }}>
                        {n.title}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          color: "gray",
                          margin: 0,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        dangerouslySetInnerHTML={{ __html: n.message }}
                      ></p>

                      <p style={{ fontSize: "11px", color: "black", marginTop: "5px" }}>
                        {new Date(n.createdAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </>
              ))}
            </>
          ) : (
            <p>No New Notification Found</p>
          )}
        </div>
      </div>

      {modalOpen && selectedNotification && (
        <MDModal open={modalOpen} onClose={() => setModalOpen(false)} width={700}>
          <div style={{ padding: "5px 10px" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setModalOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#333",
                  color: "white",
                  width: "35px",
                  height: "35px",
                  border: "none",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: "18px",
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
                  transition: "background-color 0.3s ease",
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#555")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#333")}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <h3 style={{ marginBottom: "10px" }}>{selectedNotification.title}</h3>
            <p
              style={{
                fontSize: "17px",

                margin: 0,
              }}
              dangerouslySetInnerHTML={{ __html: selectedNotification.message }}
            ></p>
          </div>
        </MDModal>
      )}
    </>
  );
};

export default AppNav;
